import { useEffect, useMemo, useRef, useState } from 'react';
import { useRemoveCannedTallyItems, useUpdateCart } from '~/api';
import { useGetPlatformProductCannedTallies } from '~/api/canned-tally';
import type { OrderItem } from '~/graphql/graphql';
import { useDebouncedQuantityCommit } from '~/hooks/useDebouncedQuantityCommit';
import {
  type ClientError,
  extractGraphQLErrorMessages,
  toastGraphQLError,
} from '~/utils';
import {
  buildTallyBundlesByFt,
  calcItemLoads,
  computeTallyCount,
  getItemWholeFt,
  getVehiclePayload,
} from '~/utils/order';

export type CannedTallyGroupParams = {
  items: OrderItem[];
  tallyPlatformProductId: string | undefined;
  organizationId: string;
  transportVehicleId: string | undefined;
  cartId: string;
  locationId: string;
  sellerOrganizationFacilityId: string;
  parentOrderId?: string | null;
  isFromQuote: boolean;
};

const isStaleTransaction = (error: unknown) =>
  extractGraphQLErrorMessages(error as ClientError).includes(
    'Stale transaction'
  );

const STALE_RETRY_DELAY_MS = 150;

// A vehicle switch's own UpdateCartTransport can race tally writes on the cart row.
async function retryOnceIfStale<T>(write: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } catch (error) {
    if (!isStaleTransaction(error)) throw error;
    await new Promise((resolve) => setTimeout(resolve, STALE_RETRY_DELAY_MS));
    return write();
  }
}

const reconcileQueues = new Map<string, Promise<void>>();

/**
 * State and cart writes for one Tally Group: resolves its Canned Tally, splits covered
 * lines from standalone ones, and owns the tally count and its debounced save.
 *
 * @example
 *   const group = useCannedTallyGroup({ items, tallyPlatformProductId, ... });
 *   <TallyCountStepper value={group.tallyCount} onChange={group.changeTallyCount} />
 */
export function useCannedTallyGroup({
  items,
  tallyPlatformProductId,
  organizationId,
  transportVehicleId,
  cartId,
  locationId,
  sellerOrganizationFacilityId,
  parentOrderId,
  isFromQuote,
}: CannedTallyGroupParams) {
  const { mutateAsync: updateCartAsync, isPending: isUpdating } =
    useUpdateCart();
  const { mutate: removeCannedTallyItems } = useRemoveCannedTallyItems();
  const [isReconciling, setIsReconciling] = useState(false);

  const {
    data: tallyData,
    isLoading: isTallyLoading,
    isError: isTallyError,
  } = useGetPlatformProductCannedTallies(
    tallyPlatformProductId,
    transportVehicleId,
    organizationId
  );
  const cannedTallyId = items.find((item) => item.cannedTally?.id)?.cannedTally
    ?.id;
  const tallies = tallyData?.platformProductCannedTallies ?? [];
  const matchedTally = tallies.find(
    (candidate) => candidate.id === cannedTallyId
  );
  const switchTarget = matchedTally ?? tallies[0];
  const tally = matchedTally;

  const tallyBundlesByFt = useMemo(
    () => buildTallyBundlesByFt(tally?.bundles),
    [tally]
  );

  const isTallyUnresolved =
    !tallyPlatformProductId ||
    !transportVehicleId ||
    isTallyLoading ||
    (isTallyError && !tallyData) ||
    (!tally && tallies.length > 0 && isReconciling);

  const { tallyItems, standaloneItems } = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) =>
        (a.platformProductsLengths?.length?.actualLengthInches ?? 0) -
        (b.platformProductsLengths?.length?.actualLengthInches ?? 0)
    );
    const covered: OrderItem[] = [];
    const standalone: OrderItem[] = [];
    if (isTallyUnresolved) return { tallyItems: sorted, standaloneItems: [] };
    for (const item of sorted) {
      const wholeFt = getItemWholeFt(item);
      if (
        item.tallyPlatformProduct?.id &&
        wholeFt !== null &&
        (tallyBundlesByFt[wholeFt] ?? 0) > 0
      )
        covered.push(item);
      else standalone.push(item);
    }
    return { tallyItems: covered, standaloneItems: standalone };
  }, [items, tallyBundlesByFt, isTallyUnresolved]);

  const standaloneItemIds = useMemo(
    () =>
      new Set(standaloneItems.map((item) => item.platformProductsLengths.id)),
    [standaloneItems]
  );

  const computedTallyCount = useMemo(
    () => computeTallyCount(tallyItems, tallyBundlesByFt),
    [tallyItems, tallyBundlesByFt]
  );

  const loadsCovered = useMemo(() => {
    if (!transportVehicleId) return null;
    const totalLoads = items.reduce(
      (acc, item) =>
        acc +
        calcItemLoads(
          item,
          getVehiclePayload(
            item.platformProductsLengths?.platformProductVehicleCapacities,
            transportVehicleId
          )
        ),
      0
    );
    return totalLoads === 0 ? null : totalLoads;
  }, [items, transportVehicleId]);

  const [tallyCount, setTallyCount] = useState(computedTallyCount);
  // A save's own cart refetch can land after a later save applied a newer count.
  const pendingConfirmedCountRef = useRef<number | null>(null);
  const latestCountRef = useRef(tallyCount);
  useEffect(() => {
    latestCountRef.current = tallyCount;
  }, [tallyCount]);
  useEffect(() => {
    if (tally) setIsReconciling(false);
  }, [tally]);

  const tallySuggestedPrice =
    tallyItems.find((item) => item.suggestedPrice?.amount != null)
      ?.suggestedPrice?.amount ?? undefined;

  const upsertTally = (tallyId: string, count: number) =>
    updateCartAsync({
      locationId,
      sellerOrganizationFacilityId,
      platformProductInputFilter: { id: tallyId, tally: true },
      quantity: { value: count, unit: 'TALLIES' },
      ...(tallySuggestedPrice != null
        ? { suggestedPrice: tallySuggestedPrice }
        : {}),
      ...(parentOrderId ? { parentOrderId } : {}),
    });

  const { trigger: triggerTallyCountCommit, cancelPending } =
    useDebouncedQuantityCommit<number>(
      async (count) => {
        if (!tally) {
          pendingConfirmedCountRef.current = null;
          return;
        }
        await upsertTally(tally.id, count);
      },
      (_count, outcome, isStale, error) => {
        if (outcome !== 'error') return;
        toastGraphQLError(error);
        if (!isStale) {
          setTallyCount(computedTallyCount);
          pendingConfirmedCountRef.current = null;
        }
      },
      500,
      { flushOnUnmount: true }
    );

  // Lines can't be edited one length at a time, so a vehicle switch replaces the whole Tally Group.
  const prevTallyRef = useRef<{ id: string; vehicleId: string } | null>(null);
  useEffect(() => {
    if (
      isFromQuote ||
      isTallyLoading ||
      isTallyError ||
      !tallyPlatformProductId ||
      !transportVehicleId
    )
      return;
    const previous = prevTallyRef.current;
    const currentId = switchTarget?.id ?? null;

    if (
      previous &&
      previous.vehicleId !== transportVehicleId &&
      previous.id !== currentId
    ) {
      cancelPending();
      const count = latestCountRef.current;
      const next = currentId
        ? { id: currentId, vehicleId: transportVehicleId }
        : null;
      prevTallyRef.current = next;
      if (!currentId) return;
      setIsReconciling(true);
      const reconcile = async () => {
        try {
          pendingConfirmedCountRef.current = count;
          await retryOnceIfStale(() => upsertTally(currentId, count));
        } catch (error) {
          pendingConfirmedCountRef.current = null;
          setIsReconciling(false);
          toastGraphQLError(error);
          if (prevTallyRef.current === next) prevTallyRef.current = previous;
        }
      };
      reconcileQueues.set(
        cartId,
        (reconcileQueues.get(cartId) ?? Promise.resolve()).then(
          reconcile,
          reconcile
        )
      );
      return;
    }

    prevTallyRef.current = currentId
      ? { id: currentId, vehicleId: transportVehicleId }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconciles only when the resolved tally or vehicle settles, not on every count or items change
  }, [switchTarget?.id, isTallyLoading, isTallyError, transportVehicleId]);

  useEffect(() => {
    if (isTallyUnresolved || !tally) return;
    setTallyCount((prev) => {
      const pending = pendingConfirmedCountRef.current;
      if (pending != null && computedTallyCount !== pending) return prev;
      pendingConfirmedCountRef.current = null;
      return computedTallyCount;
    });
  }, [computedTallyCount, isTallyUnresolved, tally]);

  const changeTallyCount = (requestedCount: number) => {
    const newCount = Math.max(1, requestedCount);
    if (isFromQuote || !tally || newCount === tallyCount) return;
    setTallyCount(newCount);
    pendingConfirmedCountRef.current = newCount;
    triggerTallyCountCommit(newCount);
  };

  const removeTallyGroup = () => {
    cancelPending();
    if (!tallyPlatformProductId) return;
    removeCannedTallyItems(
      { cartId, tallyPlatformProductId },
      { onError: toastGraphQLError }
    );
  };

  return {
    tally,
    tallyBundlesByFt,
    tallyItems,
    standaloneItems,
    standaloneItemIds,
    tallyCount,
    changeTallyCount,
    loadsCovered,
    isUpdating,
    removeTallyGroup,
  };
}
